// Copied from platform-app components/pos/customerFacing/CustomerFacingTab.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React, { useState, useRef, useEffect } from 'react';
import { Monitor, Sun, Moon } from 'lucide-react';
import type { APosCustomerDisplayConfig } from '@platform/shared-types';
import CustomerDisplayPreview, { type PreviewScreen, type PreviewTheme } from './CustomerDisplayPreview';
import { uploadsApi } from '../../../services/posApiService';

import { RubyGemMark } from '../../RubyLoader';

/**
 * POS Defaults: this copy edits the GLOBAL RubyTill default, not one business.
 * A default must never carry a store's logo, name or uploads, so those
 * controls are replaced by a note; everything else is unchanged.
 */
const DEFAULTS_MODE = true;
/**
 * The thank-you marks (2026-09-20). Emoji are gone: the customer display is the
 * screen a shopper judges the store by, so these are vector marks that take the
 * display's own colours. Mirrors ThankYouAnimation in AGENTIC_POS
 * CustomerThankYouScreen.tsx — add to both, and map any retired key there.
 */
const THANKYOU_ANIMATIONS = [
  { key: 'check',   label: 'Check',   hint: 'Draws a tick, then rests' },
  { key: 'ripple',  label: 'Ripple',  hint: 'Calm rings, loops gently' },
  { key: 'burst',   label: 'Burst',   hint: 'Rays sweep out once' },
  { key: 'sparkle', label: 'Sparkle', hint: 'Stars twinkle in your accent' },
  { key: 'heart',   label: 'Heart',   hint: 'The classic red heart' },
] as const;

/** A still of each mark, drawn at swatch size for the picker. */
function AnimationSwatch({ animKey, accent }: { animKey: string; accent: string }) {
  const ok = '#15803d', okChip = '#dcfce7';
  const tick = <polyline points="37,50 46,60 65,39" fill="none" stroke={ok} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />;
  return (
    <svg viewBox="0 0 100 100" className="w-7 h-7" aria-hidden="true">
      {animKey === 'check' && (<><circle cx="50" cy="50" r="46" fill={okChip} /><circle cx="50" cy="50" r="42" fill="none" stroke={ok} strokeWidth="5" />{tick}</>)}
      {animKey === 'ripple' && (<><circle cx="50" cy="50" r="44" fill="none" stroke={ok} strokeWidth="3" opacity=".35" /><circle cx="50" cy="50" r="37" fill="none" stroke={ok} strokeWidth="3" opacity=".6" /><circle cx="50" cy="50" r="30" fill={okChip} />{tick}</>)}
      {animKey === 'burst' && (<>{Array.from({ length: 12 }, (_, i) => { const a = (i * 30 * Math.PI) / 180; return <line key={i} x1={50 + Math.cos(a) * 32} y1={50 + Math.sin(a) * 32} x2={50 + Math.cos(a) * 46} y2={50 + Math.sin(a) * 46} stroke={accent} strokeWidth="5" strokeLinecap="round" />; })}<circle cx="50" cy="50" r="27" fill={okChip} />{tick}</>)}
      {animKey === 'sparkle' && (<><path d="M50 16 L56 42 L82 50 L56 58 L50 84 L44 58 L18 50 L44 42 Z" fill={accent} /><path d="M78 20 L81 30 L91 33 L81 36 L78 46 L75 36 L65 33 L75 30 Z" fill={accent} opacity=".6" /><path d="M22 62 L25 72 L35 75 L25 78 L22 88 L19 78 L9 75 L19 72 Z" fill={accent} opacity=".6" /></>)}
      {animKey === 'heart' && (<path d="M50 87 C20 65 5 47 5 29 C5 16 15 7 27 7 C37 7 45 14 50 23 C55 14 63 7 73 7 C85 7 95 16 95 29 C95 47 80 65 50 87 Z" fill="#ef4444" />)}
    </svg>
  );
}

interface Props {
  initialConfig: APosCustomerDisplayConfig;
  terminalType: string;
  onSave: (config: APosCustomerDisplayConfig) => Promise<void>;
  saving: boolean;
  /** The terminal's POS (till) theme — seeds the preview while the display follows the till. Default light. */
  posTheme?: 'light' | 'dark';
}

const ACCENT_PRESETS = [
  { color: '#DC143C', label: 'Ruby' },
  { color: '#10b981', label: 'Emerald' },
  { color: '#6366f1', label: 'Indigo' },
  { color: '#ef4444', label: 'Red' },
  { color: '#f59e0b', label: 'Amber' },
  { color: '#0ea5e9', label: 'Sky' },
];

const QR_TARGETS = [
  { value: 'loyalty', label: 'Loyalty sign-up' },
  { value: 'storefront', label: 'Storefront' },
  { value: 'menu', label: 'Menu' },
  { value: 'custom', label: 'Custom URL' },
] as const;

const SCREEN_EFFECTS = [
  { value: 'none',      label: 'None',      emoji: ''   },
  { value: 'snow',      label: 'Snow',      emoji: '❄️'  },
  { value: 'confetti',  label: 'Confetti',  emoji: '🎉'  },
  { value: 'hearts',    label: 'Hearts',    emoji: '💕'  },
  { value: 'fireworks', label: 'Fireworks', emoji: '🎆'  },
  { value: 'leaves',    label: 'Leaves',    emoji: '🍂'  },
  { value: 'halloween', label: 'Halloween', emoji: '🎃'  },
  { value: 'shamrocks', label: 'Shamrocks', emoji: '☘️'  },
] as const;

type ThemeChoice = 'match' | 'light' | 'dark';

const THEME_OPTIONS = [
  { value: 'match', label: 'Match till', Icon: Monitor },
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
] as const;

const THEME_NOTES: Record<ThemeChoice, string> = {
  match: DEFAULTS_MODE ? "Follows the till's POS Theme. Pick Light or Dark to fix the display to one theme." : "Follows this terminal's POS Theme. Pick Light or Dark to fix the display to one theme.",
  light: 'Always Light, whatever the POS Theme. Pick Match till to follow the terminal again.',
  dark: 'Always Dark, whatever the POS Theme. Pick Match till to follow the terminal again.',
};

const PREVIEW_SCREENS: { value: PreviewScreen; label: string }[] = [
  { value: 'idle', label: 'Idle' },
  { value: 'cart', label: 'Cart' },
  { value: 'confirm', label: 'Confirm' },
  { value: 'tip', label: 'Tip' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'thankyou', label: 'Thank you' },
  { value: 'refund', label: 'Refund' },
];

/** Absent or true = follow the till (D2 semantics); false = pinned to `theme`. */
function themeChoiceOf(c: APosCustomerDisplayConfig): ThemeChoice {
  if (c.followCashierTheme !== false) return 'match';
  return c.theme === 'dark' ? 'dark' : 'light';
}

function applyThemeChoice(c: APosCustomerDisplayConfig, choice: ThemeChoice): APosCustomerDisplayConfig {
  if (choice === 'match') return { ...c, followCashierTheme: true };
  return { ...c, theme: choice, followCashierTheme: false };
}

export default function CustomerFacingTab({ initialConfig, terminalType, onSave, saving, posTheme }: Props) {
  const [config, setConfig] = useState<APosCustomerDisplayConfig>(initialConfig);
  const [dirty, setDirty] = useState(false);
  const [previewScreen, setPreviewScreen] = useState<PreviewScreen>('idle');
  // Preview-only: which theme to show while the display follows the till. Never saved.
  // Following the till → start on the terminal's POS theme (what the display will
  // actually show); pinned → the display's own stored theme.
  const [previewTheme, setPreviewTheme] = useState<PreviewTheme>(() =>
    initialConfig.followCashierTheme !== false
      ? (posTheme ?? 'light')
      : (initialConfig.theme === 'dark' ? 'dark' : 'light'));
  // The terminal theme can arrive/change after mount (register load, POS Theme save).
  const followingRef = useRef(initialConfig.followCashierTheme !== false);
  useEffect(() => {
    if (posTheme && followingRef.current) setPreviewTheme(posTheme);
  }, [posTheme]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tyUploading, setTyUploading] = useState(false);
  const [tyUploadError, setTyUploadError] = useState<string | null>(null);
  const tyFileInputRef = useRef<HTMLInputElement>(null);

  // Shared upload: sign → PUT to storage → return the public file URL.
  const uploadImageFile = async (
    file: File,
    folder: string,
    setBusy: (b: boolean) => void,
    setErr: (e: string | null) => void,
    apply: (fileUrl: string) => void,
  ) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setErr('Please choose an image file.'); return; }
    setBusy(true);
    setErr(null);
    try {
      const { uploadUrl, fileUrl } = await uploadsApi.sign({ fileName: file.name, contentType: file.type, folder });
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      apply(fileUrl);
    } catch {
      setErr('Upload failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const uploadLogo = (file: File) =>
    uploadImageFile(file, 'customer-display-logos', setUploading, setUploadError,
      (fileUrl) => patch((c) => ({ ...c, idle: { ...c.idle, logoUrl: fileUrl } })));

  const uploadThankYouImage = (file: File) =>
    uploadImageFile(file, 'customer-display-thankyou', setTyUploading, setTyUploadError,
      (fileUrl) => patch((c) => ({ ...c, thankYou: { ...c.thankYou, message: c.thankYou?.message || '', showLogo: c.thankYou?.showLogo ?? true, imageUrl: fileUrl } })));

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (file) uploadLogo(file);
  };
  const handleThankYouImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (file) uploadThankYouImage(file);
  };
  const onDropFile = (e: React.DragEvent, handler: (f: File) => void) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0]; if (file) handler(file);
  };

  const isDual = terminalType === 'android_dual_screen';

  const themeChoice = themeChoiceOf(config);
  followingRef.current = themeChoice === 'match';
  const effectivePreviewTheme: PreviewTheme = themeChoice === 'match' ? previewTheme : themeChoice;

  const patch = (fn: (c: APosCustomerDisplayConfig) => APosCustomerDisplayConfig) => {
    setConfig((prev) => fn(prev));
    setDirty(true);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(420px,560px)] gap-6 items-start">
      {/* ── LEFT: settings ── */}
      <div>
        {/* Capability banner */}
        <div
          data-testid="cfp-banner"
          data-variant={DEFAULTS_MODE || isDual ? 'ok' : 'warn'}
          className={`flex items-start gap-3 rounded-xl p-3 mb-5 text-sm border ${
            DEFAULTS_MODE
              ? 'bg-stone-50 border-stone-200 text-stone-700'
              : isDual
              ? 'bg-green-50 border-green-200 text-green-900'
              : 'bg-amber-50 border-amber-200 text-amber-900'
          }`}
        >
          <span
            className={`flex-none w-5 h-5 rounded-md grid place-items-center text-xs text-white font-bold ${
              DEFAULTS_MODE || isDual ? 'bg-accent-500' : 'bg-amber-400'
            }`}
          >
            {DEFAULTS_MODE ? 'i' : isDual ? '✓' : '!'}
          </span>
          <div>
            {DEFAULTS_MODE ? (
              <>
                <strong>RubyTill default.</strong>{' '}
                Used by every customer-facing (Android dual-screen) device of a business that hasn&apos;t saved its own.
              </>
            ) : isDual ? (
              <>
                <strong>This device has a customer-facing screen.</strong>{' '}
                These settings publish to this register and take effect on its second screen.
              </>
            ) : (
              <>
                <strong>This terminal has no customer-facing screen.</strong>{' '}
                Settings are saved but only take effect on customer-facing (Android dual-screen) devices.
              </>
            )}
          </div>
        </div>

        {/* ── Theme card ── */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-4">
          <h3 className="text-xs font-bold uppercase tracking-wide text-stone-700 mb-1">Theme</h3>
          <p className="text-xs text-stone-400 mb-4">Skin for the whole customer display — idle, cart, payment, thank-you.</p>

          {/* Theme segmented: Match till / Light / Dark */}
          <div
            role="group"
            aria-label="Customer display theme"
            className="inline-flex bg-stone-100 border border-stone-200 rounded-xl p-1 gap-1"
          >
            {THEME_OPTIONS.map(({ value, label, Icon }) => {
              const on = themeChoice === value;
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={on}
                  data-testid={`cfp-theme-${value}`}
                  onClick={() => patch((c) => applyThemeChoice(c, value))}
                  className={`flex items-center justify-center gap-1.5 min-h-[44px] min-w-[44px] px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
                    on
                      ? 'bg-white text-stone-900 shadow-sm'
                      : 'text-stone-500'
                  }`}
                >
                  <Icon size={14} aria-hidden />
                  {label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-stone-500 mt-1.5 mb-4">{THEME_NOTES[themeChoice]}</p>

          {/* Accent colour */}
          <div>
            <label id="cfp-accent-label" className="block text-xs font-semibold text-stone-700 mb-2">Accent colour</label>
            <div role="group" aria-labelledby="cfp-accent-label" className="flex gap-2">
              {ACCENT_PRESETS.map(({ color, label }) => (
                <button
                  key={color}
                  type="button"
                  aria-label={label}
                  onClick={() => patch((c) => ({ ...c, accentColor: color }))}
                  style={{ background: color }}
                  className={`w-8 h-8 rounded-lg border-2 transition-all ${
                    config.accentColor === color
                      ? 'border-stone-900 ring-2 ring-stone-900/20'
                      : 'border-white shadow-sm'
                  }`}
                />
              ))}
            </div>
            <p className="text-xs text-stone-500 mt-1.5">Used for buttons, the selected tip and the rewards card on every screen.</p>
          </div>
        </div>

        {/* ── Idle & Welcome card ── */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-4">
          <h3 className="text-xs font-bold uppercase tracking-wide text-stone-700 mb-1">Idle &amp; Welcome screen</h3>
          <p className="text-xs text-stone-400 mb-4">What customers see between sales.</p>

          {/* Logo URL + Upload — POS Defaults: never on the default */}
          {DEFAULTS_MODE ? (
            <div className="mb-4">
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">Business logo</label>
              <div className="rounded-xl bg-stone-50 border border-stone-200 px-3 py-2 text-xs text-stone-500">
                Each business&apos;s own logo shows here — the default never carries one.
              </div>
            </div>
          ) : (
          <div className="mb-4">
            <label htmlFor="cfp-logo-url" className="block text-xs font-semibold text-stone-700 mb-1.5">
              Business logo
            </label>

            {/* Upload dropzone + thumbnail */}
            <div className="flex items-center gap-3 mb-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoFileChange}
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDropFile(e, uploadLogo)}
                className="flex-1 cursor-pointer rounded-xl border-2 border-dashed border-stone-300 hover:border-accent-400 hover:bg-accent-50/40 transition-colors px-4 py-3 text-center"
              >
                <div className="text-xs font-semibold text-stone-700">
                  {uploading ? <span className="inline-flex items-center gap-2"><RubyGemMark size={14} drawing />Uploading…</span> : '📤 Click to upload or drag an image here'}
                </div>
                <div className="text-[11px] text-stone-400 mt-0.5">PNG / SVG / JPG · or paste a URL below</div>
              </div>
              {config.idle.logoUrl && (
                <div className="w-14 h-14 rounded-lg border border-stone-200 overflow-hidden bg-stone-100 flex-none">
                  <img
                    src={config.idle.logoUrl}
                    alt="Logo preview"
                    style={{ width: '100%', height: '100%', objectFit: (config.idle.logoFit || 'contain') as 'contain' | 'cover' }}
                  />
                </div>
              )}
            </div>
            {uploadError && (
              <p className="text-xs text-red-600 mb-2">{uploadError}</p>
            )}

            {/* URL input */}
            <input
              id="cfp-logo-url"
              type="url"
              placeholder="https://… (or upload above)"
              className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
              value={config.idle.logoUrl || ''}
              onChange={(e) =>
                patch((c) => ({ ...c, idle: { ...c.idle, logoUrl: e.target.value || null } }))
              }
            />
            <p className="text-[11px] text-stone-400 mt-1">PNG / SVG · transparent background · 512×512 recommended</p>
          </div>

          )}

          {/* Logo fit */}
          <div className="mb-4">
            <label className="block text-xs font-semibold text-stone-700 mb-1.5">Logo fit</label>
            <div className="inline-flex bg-stone-100 border border-stone-200 rounded-xl p-1 gap-1">
              {([
                { value: 'contain', label: 'Fit whole logo' },
                { value: 'cover', label: 'Fill / crop' },
              ] as const).map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  data-testid={`cfp-logofit-${value}`}
                  onClick={() => patch((c) => ({ ...c, idle: { ...c.idle, logoFit: value } }))}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
                    (config.idle.logoFit ?? 'contain') === value
                      ? 'bg-white text-stone-900 shadow-sm'
                      : 'text-stone-500'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Store name — POS Defaults: each business's own name, never the default's */}
          {DEFAULTS_MODE ? (
            <div className="mb-4">
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">Store name</label>
              <div className="rounded-xl bg-stone-50 border border-stone-200 px-3 py-2 text-xs text-stone-500">
                Each business&apos;s own name shows here.
              </div>
            </div>
          ) : (
          <div className="mb-4">
            <label htmlFor="cfp-store-name" className="block text-xs font-semibold text-stone-700 mb-1.5">
              Store name
            </label>
            <input
              id="cfp-store-name"
              type="text"
              aria-label="Store name"
              className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
              value={config.idle.storeName || ''}
              onChange={(e) =>
                patch((c) => ({ ...c, idle: { ...c.idle, storeName: e.target.value } }))
              }
            />
          </div>

          )}

          {/* Tagline */}
          <div className="mb-4">
            <label htmlFor="cfp-tagline" className="block text-xs font-semibold text-stone-700 mb-1.5">
              Tagline
            </label>
            <input
              id="cfp-tagline"
              type="text"
              className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
              value={config.idle.tagline || ''}
              onChange={(e) =>
                patch((c) => ({ ...c, idle: { ...c.idle, tagline: e.target.value } }))
              }
            />
          </div>
        </div>

        {/* ── Promo banner & QR card ── */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-4">
          <h3 className="text-xs font-bold uppercase tracking-wide text-stone-700 mb-4">Promo banner &amp; QR</h3>

          {/* Promo toggle */}
          <div className="mb-4">
            <button
              type="button"
              role="switch"
              aria-checked={!!config.promo?.enabled}
              onClick={() =>
                patch((c) => ({
                  ...c,
                  promo: { ...c.promo, enabled: !c.promo?.enabled, text: c.promo?.text || '', placement: c.promo?.placement || 'both' },
                }))
              }
              className="flex items-center gap-2 text-xs font-semibold text-stone-700"
            >
              <span
                className={`relative inline-block w-9 h-5 rounded-full transition-colors ${
                  config.promo?.enabled ? 'bg-accent-500' : 'bg-stone-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    config.promo?.enabled ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </span>
              Show promo banner
            </button>

            {config.promo?.enabled && (
              <div className="mt-3 space-y-3">
                <input
                  type="text"
                  placeholder="Promo message…"
                  className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
                  value={config.promo?.text || ''}
                  onChange={(e) =>
                    patch((c) => ({ ...c, promo: { ...c.promo!, text: e.target.value } }))
                  }
                />
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">Where it shows</label>
                  <div className="inline-flex bg-stone-100 border border-stone-200 rounded-xl p-1 gap-1">
                    {([
                      { value: 'idle', label: 'Idle only' },
                      { value: 'both', label: 'Idle + Cart footer' },
                    ] as const).map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          patch((c) => ({ ...c, promo: { ...c.promo!, placement: value } }))
                        }
                        className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
                          config.promo?.placement === value
                            ? 'bg-white text-stone-900 shadow-sm'
                            : 'text-stone-500'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <hr className="border-stone-200 my-4" />

          {/* QR toggle */}
          <div>
            <button
              type="button"
              role="switch"
              aria-checked={!!config.qr?.enabled}
              onClick={() =>
                patch((c) => ({
                  ...c,
                  qr: { ...c.qr, enabled: !c.qr?.enabled, target: c.qr?.target || 'loyalty' },
                }))
              }
              className="flex items-center gap-2 text-xs font-semibold text-stone-700"
            >
              <span
                className={`relative inline-block w-9 h-5 rounded-full transition-colors ${
                  config.qr?.enabled ? 'bg-accent-500' : 'bg-stone-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    config.qr?.enabled ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </span>
              Show QR code
            </button>

            {config.qr?.enabled && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">QR links to</label>
                  <div className="inline-flex flex-wrap bg-stone-100 border border-stone-200 rounded-xl p-1 gap-1">
                    {QR_TARGETS.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          patch((c) => ({ ...c, qr: { ...c.qr!, target: value } }))
                        }
                        className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                          config.qr?.target === value
                            ? 'bg-white text-stone-900 shadow-sm'
                            : 'text-stone-500'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {config.qr?.target === 'custom' && (
                  <input
                    type="url"
                    placeholder="https://…"
                    className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
                    value={config.qr?.customUrl || ''}
                    onChange={(e) =>
                      patch((c) => ({ ...c, qr: { ...c.qr!, customUrl: e.target.value } }))
                    }
                  />
                )}
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">QR position on screen</label>
                  <div className="grid grid-cols-3 gap-1.5 max-w-[340px]">
                    {([
                      { value: 'top-left', label: '↖ Top L' },
                      { value: 'top-center', label: '↑ Top C' },
                      { value: 'top-right', label: '↗ Top R' },
                      { value: 'bottom-left', label: '↙ Bot L' },
                      { value: 'bottom-center', label: '↓ Bot C' },
                      { value: 'bottom-right', label: '↘ Bot R' },
                    ] as const).map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => patch((c) => ({ ...c, qr: { ...c.qr!, position: value } }))}
                        className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                          (config.qr?.position ?? 'top-right') === value
                            ? 'bg-accent-500 text-white border-accent-500'
                            : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Thank-you card ── */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-4">
          <h3 className="text-xs font-bold uppercase tracking-wide text-stone-700 mb-4">Thank-you screen</h3>

          {/* Thank-you visual segmented picker */}
          <div className="mb-4">
            <label className="block text-xs font-semibold text-stone-700 mb-1.5">Thank-you visual</label>
            <div className="inline-flex bg-stone-100 border border-stone-200 rounded-xl p-1 gap-1">
              {([
                { value: 'animation', label: 'Animation' },
                { value: 'logo',      label: 'Business logo' },
                { value: 'image',     label: 'Picture' },
              ] as const).map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  data-testid={`cfp-ty-visual-${value}`}
                  onClick={() =>
                    patch((c) => ({
                      ...c,
                      thankYou: {
                        ...c.thankYou,
                        message: c.thankYou?.message || '',
                        showLogo: c.thankYou?.showLogo ?? true,
                        visual: value,
                      },
                    }))
                  }
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
                    (config.thankYou?.visual ?? 'animation') === value
                      ? 'bg-white text-stone-900 shadow-sm'
                      : 'text-stone-500'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Animation picker */}
          {(config.thankYou?.visual ?? 'animation') === 'animation' && (
            <div className="mb-4">
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">Animation</label>
              <div className="flex flex-wrap gap-2">
                {THANKYOU_ANIMATIONS.map(({ key, label, hint }) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`cfp-ty-anim-${key}`}
                    title={hint}
                    onClick={() =>
                      patch((c) => ({
                        ...c,
                        thankYou: {
                          ...c.thankYou,
                          message: c.thankYou?.message || '',
                          showLogo: c.thankYou?.showLogo ?? true,
                          animation: key,
                        },
                      }))
                    }
                    className={`flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-colors min-w-[72px] ${
                      (config.thankYou?.animation ?? 'check') === key
                        ? 'bg-white text-stone-900 border-stone-900 shadow-sm ring-2 ring-stone-900/10'
                        : 'bg-stone-50 text-stone-600 border-stone-200 hover:border-stone-400'
                    }`}
                  >
                    <AnimationSwatch animKey={key} accent={config.accentColor || '#DC143C'} />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Logo note */}
          {(config.thankYou?.visual ?? 'animation') === 'logo' && (
            <div className="mb-4 rounded-xl bg-stone-50 border border-stone-200 px-3 py-2 text-xs text-stone-500">
              {DEFAULTS_MODE ? "Shows each business's own logo." : <>Shows your business logo (set above in the Idle &amp; Welcome screen section).</>}
            </div>
          )}

          {/* Image upload */}
          {(config.thankYou?.visual ?? 'animation') === 'image' && (
            <div className="mb-4">
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">{DEFAULTS_MODE ? 'Picture URL' : 'Upload picture'}</label>
              <div className={`flex items-center gap-3 mb-2 ${DEFAULTS_MODE ? 'hidden' : ''}`}>
                <input
                  ref={tyFileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleThankYouImageChange}
                />
                <div
                  onClick={() => tyFileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => onDropFile(e, uploadThankYouImage)}
                  className="flex-1 cursor-pointer rounded-xl border-2 border-dashed border-stone-300 hover:border-accent-400 hover:bg-accent-50/40 transition-colors px-4 py-3 text-center"
                >
                  <div className="text-xs font-semibold text-stone-700">
                    {tyUploading ? <span className="inline-flex items-center gap-2"><RubyGemMark size={14} drawing />Uploading…</span> : '📤 Click to upload or drag an image here'}
                  </div>
                  <div className="text-[11px] text-stone-400 mt-0.5">or paste a URL below</div>
                </div>
                {config.thankYou?.imageUrl && (
                  <div className="w-14 h-14 rounded-lg border border-stone-200 overflow-hidden bg-stone-100 flex-none">
                    <img
                      src={config.thankYou.imageUrl}
                      alt="Thank-you image preview"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </div>
                )}
              </div>
              {tyUploadError && (
                <p className="text-xs text-red-600 mb-2">{tyUploadError}</p>
              )}
              <input
                type="url"
                placeholder={DEFAULTS_MODE ? 'https://…' : 'https://… (or upload above)'}
                className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
                value={config.thankYou?.imageUrl || ''}
                onChange={(e) =>
                  patch((c) => ({
                    ...c,
                    thankYou: {
                      ...c.thankYou,
                      message: c.thankYou?.message || '',
                      showLogo: c.thankYou?.showLogo ?? true,
                      imageUrl: e.target.value || null,
                    },
                  }))
                }
              />
              <p className="text-[11px] text-stone-400 mt-1">PNG / JPG · 512×512 recommended</p>
            </div>
          )}

          {/* Thank-you message */}
          <div className="mb-4">
            <label htmlFor="cfp-thankyou-msg" className="block text-xs font-semibold text-stone-700 mb-1.5">
              Thank-you message
            </label>
            <textarea
              id="cfp-thankyou-msg"
              className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm text-stone-900 placeholder-stone-400 resize-y min-h-[56px] focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
              value={config.thankYou?.message || ''}
              onChange={(e) =>
                patch((c) => ({
                  ...c,
                  thankYou: { ...c.thankYou, message: e.target.value, showLogo: c.thankYou?.showLogo ?? true },
                }))
              }
            />
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={config.thankYou?.showLogo !== false}
            onClick={() =>
              patch((c) => ({
                ...c,
                thankYou: {
                  ...c.thankYou,
                  message: c.thankYou?.message || '',
                  showLogo: !(c.thankYou?.showLogo !== false),
                },
              }))
            }
            className="flex items-center gap-2 text-xs font-semibold text-stone-700"
          >
            <span
              className={`relative inline-block w-9 h-5 rounded-full transition-colors ${
                config.thankYou?.showLogo !== false ? 'bg-accent-500' : 'bg-stone-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  config.thankYou?.showLogo !== false ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </span>
            Show business logo on thank-you
          </button>
        </div>

        {/* ── Screen effect card ── */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-4">
          <h3 className="text-xs font-bold uppercase tracking-wide text-stone-700 mb-1">Screen effect</h3>
          <p className="text-xs text-stone-400 mb-4">Festive or seasonal animated overlay shown on the idle and thank-you screens.</p>
          <div className="flex flex-wrap gap-2">
            {SCREEN_EFFECTS.map(({ value, label, emoji }) => (
              <button
                key={value}
                type="button"
                data-testid={`cfp-effect-${value}`}
                onClick={() => patch((c) => ({ ...c, effect: value }))}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors ${
                  (config.effect ?? 'none') === value
                    ? 'bg-white text-stone-900 border-stone-900 shadow-sm ring-2 ring-stone-900/10'
                    : 'bg-stone-50 text-stone-600 border-stone-200 hover:border-stone-400'
                }`}
              >
                {emoji && <span aria-hidden="true">{emoji}</span>}
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Save bar ── */}
        <div className="sticky bottom-0 bg-white border border-stone-200 rounded-xl px-4 py-3 flex items-center justify-between mt-2">
          <span className="text-xs text-stone-500">
            {dirty ? 'You have unsaved changes.' : 'No changes.'}
          </span>
          <button
            data-testid="cfp-save"
            type="button"
            disabled={!dirty || saving}
            onClick={() => onSave(config)}
            className="flex items-center gap-2 bg-accent-500 disabled:opacity-40 text-white px-5 py-2.5 rounded-xl text-sm font-bold transition-opacity"
          >
            {saving ? <span className="inline-flex items-center gap-2"><RubyGemMark size={14} drawing color="currentColor" />Saving…</span> : (DEFAULTS_MODE ? '💾 Save default' : '💾 Save & Publish')}
          </button>
        </div>
      </div>

      {/* ── RIGHT: live preview ── */}
      <div className="sticky top-4 bg-white border border-stone-200 rounded-2xl p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <span className="text-xs font-bold uppercase tracking-wide text-stone-500">Live preview</span>
          {themeChoice === 'match' ? (
            // Preview-only switch — never marks the tab dirty, never saved.
            <div role="group" aria-label="Preview theme" className="inline-flex items-center bg-stone-100 border border-stone-200 rounded-xl p-1 gap-1">
              {([
                { value: 'light' as const, label: 'Light', Icon: Sun },
                { value: 'dark' as const, label: 'Dark', Icon: Moon },
              ]).map(({ value, label, Icon }) => {
                const on = previewTheme === value;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setPreviewTheme(value)}
                    className={`flex items-center justify-center gap-1.5 min-h-[44px] min-w-[44px] px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      on ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'
                    }`}
                  >
                    <Icon size={13} aria-hidden />
                    {label}
                  </button>
                );
              })}
            </div>
          ) : (
            <span className="text-xs font-semibold text-stone-500">
              {themeChoice === 'dark' ? 'Dark' : 'Light'} (pinned)
            </span>
          )}
        </div>
        <div role="group" aria-label="Preview screen" className="flex flex-wrap gap-1.5 mb-3">
          {PREVIEW_SCREENS.map(({ value, label }) => {
            const on = previewScreen === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={on}
                onClick={() => setPreviewScreen(value)}
                className={`min-h-[44px] min-w-[44px] px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  on ? 'bg-accent-600 text-white' : 'bg-stone-100 text-stone-500'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="rounded-2xl overflow-hidden border-8 border-stone-900 shadow-2xl">
          <CustomerDisplayPreview config={config} screen={previewScreen} theme={effectivePreviewTheme} />
        </div>
        <p className="text-[11px] text-stone-400 text-center mt-2">
          Uses the real customer display screens.{themeChoice === 'match' ? ' With Match till, the switch previews either theme.' : ''}
        </p>
      </div>
    </div>
  );
}
